import {HttpClient} from "@angular/common/http";
import {Injectable} from "@angular/core";
import * as SustainabilityRatingGraphqlModels from "@app/graphql-typings/graphql-sustainability-ratings";
import {PagedModel} from "@app/shared/api/model/paged-model";
import {CollectionUtils} from "@shared/util/collections/collection-utils";
import {ClassTransformer} from "class-transformer";
import * as gql from 'gql-query-builder';
import IQueryBuilderOptions from "gql-query-builder/build/IQueryBuilderOptions";
import VariableOptions from "gql-query-builder/build/VariableOptions";
import {map, Observable} from "rxjs";
import {BaseService} from "../../base-service";
import {APIPageInfo, GQLPagingParamsVariable, GQLQuery, GRAPHQL_TYPES, HttpUtilityService, PagingParams} from "../../http-utility-service";
import {SustainabilityRating, SustainabilityRatingId, SustainabilityRatingImpl, SustainabilityRatingsAggregateEntityEnum, SustainabilityRatingTypeId} from "./sustainability-ratings.interface";
import {sustainabilityRatingTypeFields} from '../sustainability-rating-types.service';
import {SustainabilityRatingMeasureId} from '../measures/sustainability-rating-measures.interface';


export interface SustainabilityRatingSearchTerm {
  sustainability_rating_id?: SustainabilityRatingId;
  sustainability_rating_ids?: SustainabilityRatingId[];
  sustainability_rating_type_id?: SustainabilityRatingTypeId;
  sustainability_rating_type_ids?: SustainabilityRatingTypeId[];
  sustainability_rating_measure_id?: SustainabilityRatingMeasureId;
  sustainability_rating_measure_ids?: SustainabilityRatingMeasureId[];
  aggregate_entity?: SustainabilityRatingsAggregateEntityEnum;
  period_start_date_start?: string;
  period_start_date_end?: string;
  period_end_date_start?: string;
  period_end_date_end?: string;
  expiry_date_end?: string;
  expiry_date_start?: string;
  limit_per_site_and_type?: number;
  period?: string;
  periods?: string[];
  score?: number;
  score_gt?: number;
  score_gt_eq?: number;
  score_lt?: number;
  score_lt_eq?: number;
  scores?: number[];
  site_id?: number;
  site_ids?: number[];
}


export interface SustainabilityRatingFullSearchTerm extends SustainabilityRatingSearchTerm, PagingParams {
  aggregate_entity?: SustainabilityRatingsAggregateEntityEnum;
}


const sustainabilityRatingKeyword = 'sustainability_rating';

const sustainabilityRatingVariables: VariableOptions = {
  sustainability_rating_id: {type: GRAPHQL_TYPES.INTEGER},
  sustainability_rating_ids: {type: GRAPHQL_TYPES.INTEGER, list: true},
  sustainability_rating_type_id: {type: GRAPHQL_TYPES.INTEGER},
  sustainability_rating_type_ids: {type: GRAPHQL_TYPES.INTEGER, list: true},
  sustainability_rating_measure_id: {type: GRAPHQL_TYPES.INTEGER},
  sustainability_rating_measure_ids: {type: GRAPHQL_TYPES.INTEGER, list: true},
  aggregate_entity: {type: GRAPHQL_TYPES.SUSTAINABILITY_RATINGS_AGGREGATE_ENTITY_ENUM},
  period_start_date_start: {type: GRAPHQL_TYPES.LOCAL_DATE},
  period_start_date_end: {type: GRAPHQL_TYPES.LOCAL_DATE},
  period_end_date_start: {type: GRAPHQL_TYPES.LOCAL_DATE},
  period_end_date_end: {type: GRAPHQL_TYPES.LOCAL_DATE},
  expiry_date_start: {type: GRAPHQL_TYPES.LOCAL_DATE},
  expiry_date_end: {type: GRAPHQL_TYPES.LOCAL_DATE},
  limit_per_site_and_type: {type: GRAPHQL_TYPES.INTEGER},
  period: {type: GRAPHQL_TYPES.LOCAL_DATE_INTERVAL},
  periods: {type: GRAPHQL_TYPES.LOCAL_DATE_INTERVAL, list: true},
  score: {type: GRAPHQL_TYPES.DOUBLE},
  scores: {type: GRAPHQL_TYPES.DOUBLE, list: true},
  score_gt: {type: GRAPHQL_TYPES.DOUBLE},
  score_gt_eq: {type: GRAPHQL_TYPES.DOUBLE},
  score_lt: {type: GRAPHQL_TYPES.DOUBLE},
  score_lt_eq: {type: GRAPHQL_TYPES.DOUBLE},
  site_id: {type: GRAPHQL_TYPES.INTEGER},
  site_ids: {type: GRAPHQL_TYPES.INTEGER, list: true},
  ...GQLPagingParamsVariable
};

interface SustainabilityRatingFetchOptions {
  includeType?: boolean;
}

const sustainabilityRatingAggregateByAllFields = [
  'sustainability_rating_type_id',
  'score'
];


const sustainabilityRatingAggregateBySiteFields = [
  ...sustainabilityRatingAggregateByAllFields,
  'site_id'
];


const sustainabilityRatingFields = [
  ...sustainabilityRatingAggregateBySiteFields,
  'sustainability_rating_id',
  'msra_value',
  'created_at',
  'expiry_date',
  'last_modified_at',
  'note',
  'period_end_date',
  'period_start_date'
];


@Injectable({providedIn: 'root'})
export class SustainabilityRatingsService extends BaseService {
  public static modelMapping = {SustainabilityRating: SustainabilityRatingImpl};

  private readonly PAGE_SIZE = 500;
  private readonly DATA_KEY = sustainabilityRatingKeyword;
  private readonly DATA_KEY_PLURAL = this.DATA_KEY + 's';
  private readonly sustainabilityRatingQueryTemplate: Omit<IQueryBuilderOptions, 'fields'> = {
    operation: this.DATA_KEY_PLURAL,
    variables: sustainabilityRatingVariables
  };

  constructor(
    private http: HttpClient,
    private httpUtilityService: HttpUtilityService,
    private classTransformer: ClassTransformer
  ) {
    super();
  }

  getSustainabilityRatings(pageInfo: PagingParams,
                           searchTerm: SustainabilityRatingSearchTerm | null,
                           fetchOptions?: SustainabilityRatingFetchOptions): Observable<PagedModel<SustainabilityRating[]>> {
    if (fetchOptions?.includeType && searchTerm?.aggregate_entity === SustainabilityRatingsAggregateEntityEnum.ALL) {
      throw new Error('Cannot fetch sustainability rating type when rating aggregation is set to "ALL".');
    }
    const fullSearchTerm: SustainabilityRatingFullSearchTerm = {...searchTerm, ...pageInfo};
    const gqlQuery = this.getSustainabilityRatingsGQL(searchTerm?.aggregate_entity ?? null, fetchOptions ?? null);
    return this.httpUtilityService.graphQL<SustainabilityRatingGraphqlModels.SustainabilityRating[]>(gqlQuery, this.DATA_KEY_PLURAL, fullSearchTerm).pipe(
      map(result => ({
        data: this.mapToInstance(result[this.DATA_PREFIX]),
        count: result.count
      }))
    );
  }

  getAllSustainabilityRatings(searchTerm: SustainabilityRatingSearchTerm | null, fetchOptions?: SustainabilityRatingFetchOptions): Observable<SustainabilityRating[]> {
    return this.httpUtilityService.pageUntilComplete<SustainabilityRating[]>(
      (pageInfo: APIPageInfo) => this.getSustainabilityRatings({start_index: pageInfo.first, limit: pageInfo.rows}, searchTerm, fetchOptions),
      {first: 0, rows: this.PAGE_SIZE});
  }

  upsertSustainabilityRating(sustainabilityRating: SustainabilityRating): Observable<SustainabilityRating> {
    const path = this.getSustainabilityRatingsEndpoint();
    const ratingImpl = new SustainabilityRatingImpl();
    Object.assign(ratingImpl, sustainabilityRating);
    const ratingToUpsert = this.classTransformer.instanceToPlain(ratingImpl);
    const body = {sustainability_ratings: [ratingToUpsert]};
    return this.http.post(path, body).pipe(map(res => {
      const ratings = this.httpUtilityService.extractResponseData<SustainabilityRating[]>(res, this.DATA_KEY_PLURAL);
      const ratingPlain = CollectionUtils.only(ratings);
      return this.classTransformer.plainToInstance(SustainabilityRatingImpl, ratingPlain);
    }));
  }

  deleteSustainabilityRating(sustainabilityRating: SustainabilityRating): Observable<boolean> {
    const options = {headers: this.getHeaders()};
    return this.http.delete(this.getSustainabilityRatingsEndpoint() + '/' + sustainabilityRating.sustainability_rating_id, options)
      .pipe(map((response) => this.httpUtilityService.extractResponseData<{deleted: boolean}>(response, this.DATA_KEY).deleted));
  }

  private getSustainabilityRatingsEndpoint(): string {
    return this.getGateway() + '/sustainability-ratings';
  }

  private getSustainabilityRatingsGQL(aggregateEntity: SustainabilityRatingsAggregateEntityEnum | null, fetchOptions: SustainabilityRatingFetchOptions | null): GQLQuery {
    let fields;
    if (aggregateEntity) {
      switch (aggregateEntity) {
        case SustainabilityRatingsAggregateEntityEnum.ALL:
          fields = sustainabilityRatingAggregateByAllFields;
          break;
        case SustainabilityRatingsAggregateEntityEnum.SITE:
          fields = sustainabilityRatingAggregateBySiteFields;
          break;
        default:
          throw new Error('Unknown aggregate entity: ' + aggregateEntity);
      }
    } else {
      fields = sustainabilityRatingFields;
    }

    if (fetchOptions?.includeType) {
      fields.push({sustainability_rating_type: sustainabilityRatingTypeFields});
    }

    return gql.query({
      ...this.sustainabilityRatingQueryTemplate,
      fields
    });
  }

  private mapToInstance(rating: SustainabilityRatingGraphqlModels.SustainabilityRating[]): SustainabilityRating[];
  private mapToInstance(rating: SustainabilityRatingGraphqlModels.SustainabilityRating): SustainabilityRating;
  // eslint-disable-next-line max-len
  private mapToInstance(rating: SustainabilityRatingGraphqlModels.SustainabilityRating | SustainabilityRatingGraphqlModels.SustainabilityRating[]): SustainabilityRating | SustainabilityRating[] {
    return this.classTransformer.plainToInstance(SustainabilityRatingsService.modelMapping.SustainabilityRating, rating);
  }
}
